import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import COLORS from "../../lib/colors";
import { PageTitle } from "../../components/Layout";
import { PageSkeleton } from "../../components/Feedback";
import VisioAgenda from "../../components/VisioAgenda";
import * as Icons from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, ComposedChart, Line, Legend } from "recharts";

const eur = (n) => `${(n || 0).toLocaleString("fr-FR")} €`;
const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return new Date(y, mo - 1).toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
};

export default function AdminDashboard() {
  const [d, setD] = useState(null);
  const [a, setA] = useState(null);
  const [curAlert, setCurAlert] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setD(r.data)); api.get("/admin/dashboard/analytics").then((r) => setA(r.data)); api.get("/admin/currency-settings").then((r) => setCurAlert(r.data)).catch(() => {}); }, []);
  if (!d) return <PageSkeleton />;
  const stats = [
    { label: "Entreprises actives", value: d.stats.clients, icon: "Users" },
    { label: "À traiter", value: d.stats.a_traiter ?? d.stats.tickets, icon: "MessageSquare" },
    { label: "Projets à valider", value: d.stats.projects_pending, icon: "FolderCheck" },
    { label: "Missions à valider", value: d.stats.missions, icon: "UserCog" },
  ];
  const caData = (d.ca_by_month || []).map((x) => ({ ...x, m: monthLabel(x.month) }));
  const collabData = (d.collabs_by_month || []).map((x) => ({ ...x, m: monthLabel(x.month) }));

  return (
    <div>
      <PageTitle overline="Back-office" title="Tableau de bord" />

      {curAlert?.reminder_enabled && (curAlert.missing_this_month || []).length > 0 && (
        <div className="bg-ambre-50 border border-ambre-vif/40 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="dashboard-currency-reminder">
          <Icons.CalendarClock size={16} className="text-ambre shrink-0" />
          <span className="text-sm text-ambre-texte flex-1">Taux de change à renseigner pour ce mois : <strong>{(curAlert.missing_this_month).join(", ")}</strong>. Sans taux, les factures dans ces devises ne sont pas converties en euros.</span>
          <Link to="/admin/comptabilite/devises" className="shrink-0 inline-flex items-center gap-1 text-ambre-texte font-medium hover:underline" data-testid="dashboard-reminder-link">Renseigner les taux <Icons.ArrowRight size={16} /></Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 mb-10 stagger-children">
        <div className="bg-marine rounded-sm p-6 text-white lg:col-span-1 card-lift">
          <div className="font-display text-3xl">{eur(d.stats.ca_total)}</div>
          <div className="text-sm text-white/70 mt-1">Chiffre d'affaires total</div>
        </div>
        {stats.map((s) => {
          return (
            <div key={s.label} className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6 card-lift">
              <div className="font-display text-4xl text-marine">{s.value}</div>
              <div className="text-sm text-marine/80 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="mb-10">
        <VisioAgenda items={d.upcoming_rdvs || []} showClient
          projectPath={(v) => `/admin/projets/${v.project_id}?vue=etapes`}
          missionPath={(v) => `/admin/missions/${v.mission_id}?vue=phases`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6">
          <h2 className="font-display text-xl text-marine mb-1">Chiffre d'affaires par mois</h2>
          <p className="text-xs text-marine/80 mb-4">Basé sur les projets facturés</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={caData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.marine + "11"} vertical={false} />
              <XAxis dataKey="m" tick={{ fontSize: 12, fill: COLORS["gris-texte"] }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: COLORS["gris-texte"] }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => eur(v)} cursor={{ fill: COLORS.marine + "08" }} contentStyle={{ borderRadius: 4, border: `1px solid ${COLORS.marine}22`, fontSize: 13 }} />
              <Bar dataKey="total" fill={COLORS.marine} radius={[3, 3, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6">
          <h2 className="font-display text-xl text-marine mb-1">Collaborateurs en activité par mois</h2>
          <p className="text-xs text-marine/80 mb-4">Nombre de collaborateurs avec mission</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={collabData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.marine + "11"} vertical={false} />
              <XAxis dataKey="m" tick={{ fontSize: 12, fill: COLORS["gris-texte"] }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: COLORS["gris-texte"] }} axisLine={false} tickLine={false} width={28} />
              <Tooltip cursor={{ fill: COLORS.bordeaux + "08" }} contentStyle={{ borderRadius: 4, border: `1px solid ${COLORS.bordeaux}22`, fontSize: 13 }} />
              <Bar dataKey="count" fill={COLORS.bordeaux} radius={[3, 3, 0, 0]} maxBarSize={48}>
                {collabData.map((_, i) => <Cell key={i} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6 mb-10">
        <h2 className="font-display text-xl text-marine mb-4">Chiffre d'affaires par entreprise</h2>
        <div className="space-y-2">
          {(d.ca_by_client || []).map((c) => {
            const max = Math.max(...(d.ca_by_client || []).map((x) => x.total), 1);
            return (
              <div key={c.client} className="flex items-center gap-4">
                <div className="w-24 sm:w-48 text-sm text-marine truncate shrink-0">{c.client}</div>
                <div className="flex-1 h-6 bg-sable border border-marine/30 rounded-sm overflow-hidden">
                  <div className="h-full bg-bordeaux rounded-sm" style={{ width: `${(c.total / max) * 100}%` }} />
                </div>
                <div className="w-20 sm:w-28 text-right text-sm font-medium text-marine shrink-0">{eur(c.total)}</div>
              </div>
            );
          })}
          {(d.ca_by_client || []).length === 0 && <p className="text-sm text-marine/80">Aucune donnée.</p>}
        </div>
      </div>

      <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6 mb-10" data-testid="by-nature-section">
        <h2 className="font-display text-xl text-marine mb-1">Répartition par nature d'entreprise</h2>
        <p className="text-xs text-marine/80 mb-4">Portefeuille et chiffre d'affaires par type de structure</p>
        <div className="grid gap-2.5">
          {(d.by_nature || []).map((n) => {
            const maxCa = Math.max(...(d.by_nature || []).map((x) => x.ca), 1);
            return (
              <div key={n.nature} data-testid={`nature-stat-${n.nature}`} className="flex items-center gap-4">
                <div className="w-28 sm:w-56 shrink-0">
                  <div className="text-sm text-marine truncate">{n.nature}</div>
                  <div className="text-xs text-marine/80 truncate">{n.clients} entreprise{n.clients > 1 ? "s" : ""}</div>
                </div>
                <div className="flex-1 h-6 bg-marine-50 rounded-sm overflow-hidden">
                  <div className="h-full bg-marine rounded-sm" style={{ width: `${(n.ca / maxCa) * 100}%` }} />
                </div>
                <div className="w-20 sm:w-28 text-right text-sm font-medium text-marine shrink-0">{eur(n.ca)}</div>
              </div>
            );
          })}
          {(d.by_nature || []).length === 0 && <p className="text-sm text-marine/80">Aucune donnée.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <div className="bg-sable rounded-sm border border-bordeaux/25 shadow-sm p-6 card-lift" data-testid="client-due-card">
          <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">À encaisser des entreprises</h2></div>
          <p className="text-xs text-marine/80 mb-4">Échéances entreprises non réglées (toutes périodes)</p>
          <div className="font-display text-4xl text-bordeaux" data-testid="client-due-total">{eur(d.client_due?.total)}</div>
          {d.client_due?.overdue > 0 && <div className="mt-2 inline-flex items-center gap-1.5 text-[9px] text-rouge bg-rouge-50 border border-rouge/30 px-2.5 py-1 rounded-sm">Dont {eur(d.client_due.overdue)} en retard</div>}
          <Link to="/admin/comptabilite" className="block mt-4 text-sm text-marine hover:underline">Voir la comptabilité →</Link>
        </div>

        <div className="bg-sable rounded-sm border border-marine/25 shadow-sm p-6 card-lift" data-testid="collab-due-card">
          <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">À payer aux collaborateurs</h2><span className="text-[9px] uppercase tracking-wider bg-marine-50 text-marine px-2 py-0.5 rounded-sm flex items-center gap-1">Interne</span></div>
          <p className="text-xs text-marine/80 mb-4">Rémunérations en attente, par collaborateur</p>
          <div className="font-display text-4xl text-marine" data-testid="collab-due-total">{eur(d.collab_due?.total)}</div>
          {d.collab_due?.overdue > 0 && <div className="mt-2 mb-3 inline-flex items-center gap-1.5 text-[9px] text-rouge bg-rouge-50 border border-rouge/30 px-2.5 py-1 rounded-sm">Dont {eur(d.collab_due.overdue)} en retard</div>}
          <div className="space-y-1.5 mt-3">
            {(d.collab_due?.by_collaborator || []).slice(0, 5).map((c) => (
              <div key={c.name} className="flex items-center justify-between text-sm border-b border-marine/5 pb-1.5">
                <span className="text-marine">{c.name}</span>
                <span className="flex items-center gap-2"><span className="font-medium text-marine">{eur(c.amount)}</span>{c.overdue > 0 && <span className="text-xs text-rouge">({eur(c.overdue)} retard)</span>}</span>
              </div>
            ))}
            {(d.collab_due?.by_collaborator || []).length === 0 && <p className="text-sm text-marine/80">Rien à payer pour le moment.</p>}
          </div>
          <Link to="/admin/comptabilite?flow=out" className="block mt-4 text-sm text-marine hover:underline">Voir les décaissements dans la comptabilité →</Link>
        </div>
      </div>

      {a && (
        <div className="mb-10" data-testid="analytics-section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6" data-testid="cashflow-chart">
              <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">Évolution de la trésorerie</h2></div>
              <p className="text-xs text-marine/80 mb-4">Encaissements vs décaissements réels · 12 derniers mois</p>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={(a.cashflow || []).map((x) => ({ ...x, m: monthLabel(x.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS["sable-clair"]} vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 11, fill: COLORS["gris-texte"] }} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS["gris-texte"] }} width={45} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="encaissements" name="Encaissements" fill={COLORS.bleu} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="decaissements" name="Décaissements" fill={COLORS.bordeaux} radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="resultat" name="Résultat net" stroke={COLORS.marine} strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6" data-testid="forecast-chart">
              <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">Prévision de trésorerie</h2></div>
              <p className="text-xs text-marine/80 mb-4">Entrées / sorties attendues · 6 prochains mois (soldes restants)</p>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={(a.forecast || []).map((x) => ({ ...x, m: monthLabel(x.month) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS["sable-clair"]} vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 11, fill: COLORS["gris-texte"] }} />
                  <YAxis tick={{ fontSize: 11, fill: COLORS["gris-texte"] }} width={45} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="encaissements" name="À encaisser" fill={COLORS.bleu} fillOpacity={0.55} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="decaissements" name="À payer" fill={COLORS.bordeaux} fillOpacity={0.55} radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="resultat" name="Résultat prévu" stroke={COLORS.marine} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6" data-testid="stats-clients">
              <div className="flex items-center gap-2 mb-4"><h2 className="font-display text-xl text-marine">Statistiques par entreprise</h2></div>
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-2 text-sm items-center overflow-x-auto">
                <div className="text-xs uppercase tracking-wider text-marine/80">Entreprise</div>
                <div className="text-xs uppercase tracking-wider text-marine/80 text-right">CA</div>
                <div className="text-xs uppercase tracking-wider text-bleu-fonce text-right">Encaissé</div>
                <div className="text-xs uppercase tracking-wider text-bordeaux text-right">Reste</div>
                {(a.by_client || []).slice(0, 8).map((c) => (
                  <React.Fragment key={c.name}>
                    <div className="text-marine truncate border-t border-marine/5 pt-2">{c.name}</div>
                    <div className="text-right text-marine border-t border-marine/5 pt-2">{eur(c.contracted)}</div>
                    <div className="text-right text-bleu border-t border-marine/5 pt-2">{eur(c.encaisse)}</div>
                    <div className="text-right text-bordeaux border-t border-marine/5 pt-2">{eur(c.reste)}</div>
                  </React.Fragment>
                ))}
              </div>
              {(a.by_client || []).length === 0 && <p className="text-sm text-marine/80">Aucune donnée.</p>}
            </div>

            <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6" data-testid="stats-collabs">
              <div className="flex items-center gap-2 mb-1"><h2 className="font-display text-xl text-marine">Statistiques par collaborateur</h2><span className="text-[9px] uppercase tracking-wider bg-marine-50 text-marine px-2 py-0.5 rounded-sm flex items-center gap-1">Interne</span></div>
              <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-2 text-sm items-center mt-3 overflow-x-auto">
                <div className="text-xs uppercase tracking-wider text-marine/80">Collaborateur</div>
                <div className="text-xs uppercase tracking-wider text-marine/80 text-right">Total dû</div>
                <div className="text-xs uppercase tracking-wider text-bleu-fonce text-right">Payé</div>
                <div className="text-xs uppercase tracking-wider text-bordeaux text-right">Reste</div>
                {(a.by_collaborator || []).slice(0, 8).map((c) => (
                  <React.Fragment key={c.name}>
                    <div className="text-marine truncate border-t border-marine/5 pt-2">{c.name}</div>
                    <div className="text-right text-marine border-t border-marine/5 pt-2">{eur(c.total)}</div>
                    <div className="text-right text-bleu border-t border-marine/5 pt-2">{eur(c.paid)}</div>
                    <div className="text-right text-bordeaux border-t border-marine/5 pt-2">{eur(c.reste)}</div>
                  </React.Fragment>
                ))}
              </div>
              {(a.by_collaborator || []).length === 0 && <p className="text-sm text-marine/80 mt-3">Aucune donnée.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
